import React, { useEffect, useState, useRef } from "react";
import type { InputHTMLAttributes } from "react";
import { formatPhoneNumber } from "@/validation-schemas/update-profile-schemas";

interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    id: string;
    label: string;
    error?: string;
    className?: string;
    onChange?: (value: string) => void;
    value?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
    id,
    label,
    error,
    className = "",
    onChange,
    value = "",
    ...rest
}) => {
    const [displayValue, setDisplayValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    // Format input when value changes externally (like reset/prefill)
    useEffect(() => {
        const digits = value?.replace(/\D/g, "") ?? "";
        if (digits) {
            const formatted = formatPhoneNumber(digits);
            setDisplayValue(`+1 ${formatted}`);
        } else {
            setDisplayValue("+1 ");
        }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let input = e.target.value;

        // Prevent removing +1
        if (!input.startsWith("+1 ")) {
            input = "+1 ";
        }

        // Extract just the digits
        const digits = input.replace(/\D/g, "").replace(/^1/, "").slice(0, 10); // ensure max 10 digits
        const formatted = formatPhoneNumber(digits);

        setDisplayValue(`+1 ${formatted}`);
        onChange?.(digits); // Send raw 10-digit number to form state
    };

    const handleFocus = () => {
        if (displayValue.trim() === "") {
            setDisplayValue("+1 ");
        }
    };

    return (
      <div className="relative w-full select-none">
        <input
          ref={inputRef}
          id={id}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder=" "
          className={`peer form-control block w-full rounded-md border border-white bg-white px-7 pt-[26px] pb-2.5 text-base text-gray-900
          placeholder-transparent
          focus:border-[#8c8d90] focus:outline-none focus:ring-4 focus:ring-[rgba(24,26,32,0.25)]
          ${error ? "border-red-500" : ""} ${className}`}
          {...rest}
        />
        <label
          htmlFor={id}
          className="absolute pl-[17px] pr-7 pb-4 left-3 top-2 text-sm transition-all cursor-text
          peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:text-[#181a20]
          peer-focus:top-2 peer-focus:text-sm peer-focus:opacity-65 peer-not-placeholder-shown:opacity-65"
        >
          {label}
        </label>
        {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
      </div>
    );
};
